import { GlassCard } from '@/components/ui/glass-card'
import { useSectionSequence } from '@/hooks'
import { caseStudies } from '@/data/case-studies'
import { Layers, GitBranch, ShieldCheck } from 'lucide-react'

const STUDY_ICONS = [Layers, GitBranch, ShieldCheck]

/**
 * The spine of the AI-native portfolio: three production AI systems, each framed
 * problem -> architecture -> measured result. Each number traces to the
 * production source or runtime config behind it.
 */
export function CaseStudies({ heading = true }: { heading?: boolean }) {
  const ref = useSectionSequence<HTMLElement>([
    { selector: '.cs-head', direction: 'left', delay: 0 },
    { selector: '.project-card', direction: 'bottom', stagger: 0.12, delay: 0.1 },
  ])

  return (
    <section ref={ref} className="px-4 py-20 md:py-28">
      <div className="mx-auto max-w-5xl">
        {heading && (
          <div className="cs-head mb-14 text-center">
            <p className="text-primary mb-3 font-mono text-sm font-medium tracking-wide">&lt;case-studies /&gt;</p>
            <h2 className="text-fluid-h2 text-foreground mb-4">AI systems, in production</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
              Not demos. Three systems where LLMs do real work behind guardrails — running unattended, with the numbers
              below tracing to the source and runtime config behind them.
            </p>
          </div>
        )}

        <div className="space-y-8">
          {caseStudies.map((study, i) => {
            const Icon = STUDY_ICONS[i % STUDY_ICONS.length]
            return (
              <GlassCard key={study.slug} className="project-card cyber-accent-left pl-6 md:pl-8">
                <div className="flex flex-col gap-6">
                  {/* Top: title + capability */}
                  <div className="flex items-start gap-4">
                    <div className="gradient-cyber flex h-12 w-12 shrink-0 items-center justify-center rounded-xl">
                      <Icon className="text-primary-foreground h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-primary text-xs font-medium tracking-wider uppercase">{study.kicker}</p>
                      <h3 className="text-foreground mt-1 text-xl font-semibold md:text-2xl">{study.title}</h3>
                      <p className="text-muted-foreground mt-2">{study.oneLiner}</p>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="border-border/60 bg-background/30 grid grid-cols-3 gap-3 rounded-xl border p-4">
                    {study.metrics.map((m) => (
                      <div key={m.label} className="text-center">
                        <p className="text-primary text-xl font-bold md:text-2xl">{m.value}</p>
                        <p className="text-muted-foreground mt-1 text-[11px] leading-tight">{m.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Problem / Architecture / Result */}
                  <div className="grid gap-6 md:grid-cols-3">
                    <div>
                      <p className="text-foreground/70 mb-2 text-xs font-semibold tracking-wider uppercase">Problem</p>
                      <p className="text-muted-foreground text-sm">{study.problem}</p>
                    </div>
                    <div>
                      <p className="text-foreground/70 mb-2 text-xs font-semibold tracking-wider uppercase">
                        AI architecture
                      </p>
                      <ul className="space-y-2">
                        {study.architecture.map((a, j) => (
                          <li key={j} className="text-muted-foreground flex gap-2 text-sm">
                            <span className="bg-primary mt-1.5 h-1 w-1 shrink-0 rounded-full" />
                            <span>{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-foreground/70 mb-2 text-xs font-semibold tracking-wider uppercase">
                        Measured result
                      </p>
                      <p className="text-muted-foreground text-sm">{study.result}</p>
                    </div>
                  </div>

                  {/* Stack */}
                  <div className="border-border/50 flex flex-wrap items-center gap-2 border-t pt-4">
                    <span className="text-primary text-xs font-medium">{study.capability}</span>
                    <span className="text-muted-foreground/40">·</span>
                    {study.stack.map((t) => (
                      <span
                        key={t}
                        className="bg-secondary/70 text-secondary-foreground rounded px-2 py-0.5 text-[11px]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </GlassCard>
            )
          })}
        </div>
      </div>
    </section>
  )
}
